import debounce from 'debounce-promise'
import { useContext, useEffect, useMemo, useRef } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import { useDatesOrGuestsChanged } from './useDatesOrGuestsChanged'
import {
  useUpdateSavedItemBookmarkHandlers,
} from './useUpdateSavedItemSnackbarHandlers'

import { useOfferPageRate } from 'components/OfferPage/hooks/useOfferPageRate'
import { getDatesAndGuestsFromOfferPageState } from 'components/OfferPage/utils'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { intlDateRangeFormatter } from 'lib/datetime/dateUtils'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { useUpdateTripItem } from '../api/tripItem'
import { useTrip } from '../api/trip'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import type {
  AccommodationItem,
  BookmarkDatesAndOccupancy,
  LETripItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { createLEOfferItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/standard'

export const getHotelUpdateSuccessSnackbarMessage = <
  T extends {
    checkIn?: string
    checkOut?: string
    occupancies?: Array<App.Occupants>
  },
>(intl: IntlShape, {
  checkIn,
  checkOut,
  occupancies,
}: T): string => {
  const dates =
    checkIn && checkOut ?
        intlDateRangeFormatter(checkIn, checkOut, INTL_DAY_MONTH_NAME_SHORT, intl.locale) :
      undefined
  const guests = occupancies ?
      formatOccupantsAsGuests(...occupancies) :
    undefined

  if (dates && guests) {
    return intl.formatMessage(
      {
        id: 'vzS-Njg-6ZzqSOQy9arn',
        defaultMessage: 'Details updated to {dates}, {guests}',
        description: 'Snackbar message when saved hotel bookmark dates and guests are updated',
      },
      { dates, guests },
    )
  } else if (dates) {
    return intl.formatMessage(
      {
        id: 'U8nJNUZYAyuVbX_3Sg7n',
        defaultMessage: 'Details updated to {dates}',
        description: 'Snackbar message when saved hotel bookmark dates are updated',
      },
      { dates },
    )
  } else if (guests) {
    return intl.formatMessage(
      {
        id: 'qgzsxGN4Lm77YSaj49Ut',
        defaultMessage: 'Details updated to {guests}',
        description: 'Snackbar message when saved hotel bookmark guests are updated',
      },
      { guests },
    )
  } else {
    return intl.formatMessage({
      id: 'lQfS9mgfI2IOnnGnJ38q',
      defaultMessage: 'Update successful',
      description: 'Generic snackbar message when saved item is updated successfully',
    })
  }
}

export const useSyncHotelTripItem = (
  state: OfferPageState,
  offer?: App.Offer,
) => {
  const intl = useIntl()
  const awaitingFilters = useRef(false)
  const attemptUpdates = useRef(false)
  const currentTripId = useAppSelector(getImmersiveTripId)
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { showUpdateErrorSnackbar, showUpdateSuccessSnackbar } =
    useUpdateSavedItemBookmarkHandlers()

  const hotelItemRef = useRef<Array<AccommodationItem> | null>(null)
  hotelItemRef.current = currentTrip?.items.filter(
    (item: TripItem): item is AccommodationItem =>
      item.type === 'ACCOMMODATION' &&
      !item.isBooked &&
      item.code === offer?.id,
  ) ?? null

  const { mutate: updateMutate } = useUpdateTripItem({
    onSuccess: (item) => {
      // If the hotelItemRef exists, then we have a bookmarked item
      const savedHotelItem = item as Extract<
        LETripItem,
        AccommodationItem
      >
      showUpdateSuccessSnackbar(
        getHotelUpdateSuccessSnackbarMessage(intl, savedHotelItem.savedItemData),
        item.tripId,
        item.id,
      )
    },
    onError: showUpdateErrorSnackbar,
  })

  const datesAndGuests = getDatesAndGuestsFromOfferPageState(state)
  const datesOrGuestsChanged = useDatesOrGuestsChanged(datesAndGuests)
  const filtersApplied = !state.activeFilter
  const hotelRateRef = useRef<ReturnType<typeof useOfferPageRate> | undefined>(
    undefined,
  )
  const hotelRate = useOfferPageRate(state, { disabled: !offer })
  hotelRateRef.current = hotelRate
  const debouncedUpdate = useMemo(
    () =>
      debounce((datesAndGuests: BookmarkDatesAndOccupancy) => {
        const items = hotelItemRef.current
        const hotelRate = hotelRateRef.current
        if (!items || !hotelRate || !offer) return

        attemptUpdates.current = false

        items.forEach((item) => {
          const payload = createLEOfferItemPayload({
            offer,
            datesAndGuests,
            currentRegionCode,
            currencyCode: currentCurrency,
            price: hotelRate.hotelPrice,
            value: hotelRate.hotelValue,
            roomTypeId: item.subCode,
          })
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: payload,
          })
        })
      }, 2000),
    [currentCurrency, currentRegionCode, offer, updateMutate],
  )

  useEffect(() => {
    if (datesOrGuestsChanged) {
      awaitingFilters.current = true
    }
    if (awaitingFilters.current && filtersApplied) {
      awaitingFilters.current = false
      attemptUpdates.current = true
    }
    if (attemptUpdates.current) {
      debouncedUpdate(datesAndGuests)
    }
  })
}
